import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, X, Upload, Users } from "lucide-react";

export const CATEGORIES = [
  "Buyer prospects", "Seller prospects", "Buyer", "Sellers",
  "All Clients", "Real Estate Agents", "Vendors", "Open House Attendees",
];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "Buyer prospects", tag: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bulk, setBulk] = useState("");
  const [showBulk, setShowBulk] = useState(false);

  const load = () => api.get("/contacts").then(r => setContacts(r.data || []));
  useEffect(() => { load(); }, []);

  const visible = useMemo(() =>
    filter === "All" ? contacts : contacts.filter(c => c.category === filter),
  [contacts, filter]);

  const counts = useMemo(() => {
    const m = { All: contacts.length };
    for (const c of contacts) m[c.category] = (m[c.category] || 0) + 1;
    return m;
  }, [contacts]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contacts", form);
      setForm({ name: "", email: "", phone: "", category: form.category, tag: "" });
      toast.success("Contact added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/contacts/${id}`);
    load();
  };

  const startEdit = (c) => { setEditId(c.id); setEditForm({ ...c }); };
  const saveEdit = async () => {
    await api.put(`/contacts/${editId}`, editForm);
    toast.success("Saved");
    setEditId(null);
    load();
  };

  const bulkImport = async () => {
    const lines = bulk.split(/\n/).map(l => l.trim()).filter(Boolean);
    const rows = lines.map(l => {
      const [name, email, phone, category] = l.split(",").map(s => (s || "").trim());
      if (!name || !email) return null;
      return { name, email, phone: phone || "", category: category || "All Clients", tag: "" };
    }).filter(Boolean);
    if (!rows.length) return toast.error("No valid rows. Format: Name, Email, Phone, Category per line");
    const { data } = await api.post("/contacts/bulk", { contacts: rows });
    toast.success(`Imported ${data.inserted}/${data.total}`);
    setBulk("");
    setShowBulk(false);
    load();
  };

  return (
    <div className="space-y-7" data-testid="contacts-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Brokerage · CRM</div>
          <h1 className="font-heading text-5xl mt-2">Contacts List</h1>
          <p className="text-sm text-[#050303]/70 mt-2">Categorize · edit · bulk import. Feeds newsletters and property boost leads.</p>
        </div>
        <button onClick={() => setShowBulk(s => !s)} className="border border-[#050303] pill-btn text-sm flex items-center gap-2" data-testid="bulk-toggle">
          <Upload size={14} /> Bulk import
        </button>
      </header>

      {showBulk && (
        <div className="core-card p-6" data-testid="bulk-panel">
          <div className="overline mb-2">Paste one contact per line — comma separated</div>
          <div className="text-xs text-[#050303]/60 mb-2">Format: <code>Name, Email, Phone, Category</code></div>
          <textarea rows={6} value={bulk} onChange={e => setBulk(e.target.value)}
            placeholder="Jane Doe, jane@example.com, 404-555-0100, Buyer prospects&#10;Bob Smith, bob@example.com, 678-555-0102, Vendors"
            className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm font-mono"
            data-testid="bulk-textarea" />
          <button onClick={bulkImport} className="mt-3 bg-[#050303] text-white pill-btn text-sm" data-testid="bulk-import">Import</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {["All", ...CATEGORIES].map(cat => (
          <button key={cat} onClick={() => setFilter(cat)}
            className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
              filter === cat ? "bg-[#050303] text-white" : "bg-[#ffffff] border border-[#d1cece] text-[#050303]/75 hover:bg-white"
            }`} data-testid={`filter-${cat}`}>
            {cat} <span className="opacity-60">({counts[cat] || 0})</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="core-card p-6 space-y-3" data-testid="new-contact-form">
          <h3 className="font-heading text-xl flex items-center gap-2"><Plus size={16} /> New contact</h3>
          <input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="c-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="c-email" />
          <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="c-phone" />
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="c-category">
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <button type="submit" className="w-full bg-[#050303] text-white pill-btn text-sm flex items-center justify-center gap-2" data-testid="c-submit"><Users size={14} /> Add contact</button>
        </form>

        <div className="lg:col-span-2 core-card p-6">
          <h3 className="font-heading text-xl mb-4">{filter} <span className="text-[#050303]/40 text-sm font-body">· {visible.length}</span></h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {visible.length === 0 && <div className="text-sm text-[#050303]/50">No contacts in this category yet.</div>}
            {visible.map(c => editId === c.id ? (
              <div key={c.id} className="border border-[#d1cece] rounded-md p-3 space-y-2" data-testid={`edit-${c.id}`}>
                <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-2 py-1.5 border border-[#d1cece] rounded-md text-sm" />
                <input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="w-full px-2 py-1.5 border border-[#d1cece] rounded-md text-sm" />
                <input value={editForm.phone || ""} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="w-full px-2 py-1.5 border border-[#d1cece] rounded-md text-sm" />
                <select value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} className="w-full px-2 py-1.5 border border-[#d1cece] rounded-md text-sm">
                  {CATEGORIES.map(x => <option key={x}>{x}</option>)}
                </select>
                <div className="flex gap-2">
                  <button onClick={saveEdit} className="bg-[#050303] text-white pill-btn text-xs flex items-center gap-1"><Save size={12} /> Save</button>
                  <button onClick={() => setEditId(null)} className="border border-[#050303] pill-btn text-xs flex items-center gap-1"><X size={12} /> Cancel</button>
                </div>
              </div>
            ) : (
              <div key={c.id} className="flex items-start justify-between border-b border-[#d1cece] pb-2 gap-2" data-testid={`row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-xs text-[#050303]/60 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#bf858e] mt-0.5">{c.category}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(c)} className="text-[#050303]/55 hover:text-[#bf858e]" data-testid={`edit-btn-${c.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => del(c.id)} className="text-[#050303]/40 hover:text-[#bf858e]" data-testid={`del-${c.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
